import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, Self, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroupDirective } from '@angular/forms';
import { BehaviorSubject, Subject, filter, fromEvent, map, switchMap, take, takeUntil } from 'rxjs';
import { DynamicFormService } from './dynamic-form.service';
import { IControl } from './form-controls';

@Component({
  selector: 'ss-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.css'],
  providers: [
    DynamicFormService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class DynamicFormComponent implements OnInit {
  private readonly _destroy = new Subject<void>();

  @Input() defaultBtnText?: string;
  @Input() showClearBtn?: boolean;
  @Input() autoFocusFirstControl?: boolean

  readonly controlsSubject = this._dynamicFormService.controlsSubject;
  @Input() get controls() {
    return this.controlsSubject.value
  }
  set controls(value) {
    this.controlsSubject.next(value)
  }

  readonly colCountSubject = new BehaviorSubject<number>(1)
  @Input() get colCount() {
    return this.colCountSubject.value
  }
  set colCount(value) {
    this.colCountSubject.next(value)
  }

  readonly spacingSubject = new BehaviorSubject<number>(10)
  @Input() get spacing() {
    return this.spacingSubject.value
  }
  set spacing(value) {
    this.spacingSubject.next(value)
  }

  readonly formDataSubject = this._dynamicFormService.formDataSubject
  @Input() set formData(value: any) {
    if (!value) {
      return;
    }

    this.formDataSubject.next(value);
  }

  readonly applyValidationSubject = this._dynamicFormService.applyValidationSubject;
  @Input() get applyValidation() {
    return this.applyValidationSubject.value
  }
  set applyValidation(value) {
    this.applyValidationSubject.next(value)
  }

  readonly submitButtonSubject = new BehaviorSubject<HTMLButtonElement>(null)
  @Input() get submitButton() {
    return this.submitButtonSubject.value
  }
  set submitButton(value) {
    this.submitButtonSubject.next(value)
  }

  @ViewChild('formGroupDirective', { read: FormGroupDirective })
  private _formGroupDirective: FormGroupDirective


  @Output()
  readonly formSubmit = new EventEmitter<{ [key: string]: any }>()

  @Output()
  readonly mainFormGroupSubject = this._dynamicFormService.mainFormGroupSubject;
  readonly numberOfColsSubject = this._dynamicFormService.numberOfColsSubject;
  get mainFormGroup() {
    return this.mainFormGroupSubject.value;
  }

  readonly submittedSubject = this._dynamicFormService.submittedSubject;


  constructor(
    @Self() private _dynamicFormService: DynamicFormService
  ) {

  }


  ngOnInit(): void {

  }




  ngAfterViewInit() {

    if (this.autoFocusFirstControl) {

      this.mainFormGroupSubject.pipe(filter(Boolean), take(1), takeUntil(this._destroy))
        .subscribe((mainFormGroup) => {

          const firstControl = this._dynamicFormService.controls[0]
          const dataField = firstControl.dataField;
          mainFormGroup.get(dataField).focus();
        })

    }


    this.submitButtonSubject.pipe(
      filter(Boolean),
      switchMap((button) => fromEvent(button, 'click')),
      takeUntil(this._destroy)
    )
      .subscribe(() => {
        this._formGroupDirective.ngSubmit.emit(this.mainFormGroup.value)
      })
  }

  formSubmitted() {

    if (!this.mainFormGroup) {
      return;
    };

    const formData = this.mainFormGroup.value;
    const valid = this.mainFormGroup.valid;

    if (!valid) {
      this.submittedSubject.next(true);
      return;

    }

    this.formSubmit.emit(formData);
  }

  getColSpanObs(control: IControl) {

    return this.colCountSubject.pipe(
      map((colCount) => {

        if (!control.colSpan) {
          return 1;
        }

        if (control.colSpan > colCount) {
          return this.colCountSubject.value
        }

        return control.colSpan
      })
    )

  }

  ngOnDestroy() {
    this._destroy.next();
    this._destroy.complete();

  }
}
